import * as THREE from 'three';
import type { Dog } from '../dog/Dog';
import type { CareEvent } from '../care/CareRecord';

/**
 * The care loop beyond food: water, brushing, baths, the vet.
 * - thirst climbs faster than hunger; the water bowl is free to refill
 * - coat gets scruffy over days; a brush fixes it and she loves it
 * - she sometimes rolls in something in the yard; then she needs a bath, and
 *   shakes off after
 * - days of poor care leave her limping; the vet fixes that
 * All state is persisted per dog and synced (newest wins).
 */
export interface NeedsState {
  thirst: number; // 0 fine .. 1 parched
  water: number; // bowl fill 0..1
  lastBrushed: number; // ms epoch
  dirty: boolean;
  limping: boolean;
  lowCareDays: number; // consecutive days that ended with poor care
  lastDayChecked: string;
  at: number;
}

const THIRST_PERIOD = 90 * 60; // seconds from watered to parched
const SCRUFF_DAYS = 5; // days from brushed to fully scruffy
const DRINK_AT = 0.45;
const SIP_EVERY = 0.5;
const SIP_WATER = 0.05;
const SIP_THIRST = 0.12;
const REACH = 0.7;

const _p = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);

export type CareAction = 'brush' | 'bath' | 'vet';

export class CareActions {
  state: NeedsState;
  readonly waterBowl = new THREE.Group();
  onMessage?: (text: string) => void;
  onCare?: (ev: CareEvent) => void;
  onChange?: () => void;
  /** a ritual in progress (brush/bath/vet) with progress 0..1 */
  ritual: { kind: CareAction; t: number; dur: number } | null = null;

  private water: THREE.Mesh;
  /** what's in your hand during a ritual (brush / shower head / stethoscope) and its particles */
  private props = new THREE.Group();
  private drops: THREE.Points | null = null;
  private dropVel: Float32Array | null = null;
  private suds: THREE.Mesh[] = [];
  private fur: THREE.Points | null = null;
  private scene: THREE.Object3D;
  private phase: 'idle' | 'walking' | 'drinking' = 'idle';
  private sipTimer = 0;
  private rollCheck = 0;
  private saveKey: string;

  constructor(
    scene: THREE.Object3D,
    private dog: Dog,
    position: THREE.Vector3,
  ) {
    this.saveKey = `lunaloo.needs.${dog.profile.id}`;
    this.scene = scene;
    this.props.visible = false;
    scene.add(this.props);
    const dish = new THREE.Mesh(
      new THREE.CylinderGeometry(0.18, 0.14, 0.07, 24),
      new THREE.MeshStandardMaterial({ color: 0xcfd8dc, roughness: 0.4 }),
    );
    dish.position.y = 0.035;
    dish.castShadow = true;
    this.water = new THREE.Mesh(
      new THREE.CylinderGeometry(0.16, 0.16, 0.02, 24),
      new THREE.MeshStandardMaterial({ color: 0x5aa9e6, roughness: 0.1, metalness: 0.1, transparent: true, opacity: 0.85 }),
    );
    this.water.position.y = 0.06;
    this.waterBowl.add(dish, this.water);
    this.waterBowl.position.copy(position);
    this.waterBowl.name = 'water';
    this.waterBowl.userData.tapName = 'water';
    scene.add(this.waterBowl);
    this.state = this.load();
    this.applyLooks();
  }

  // ---------- state
  private load(): NeedsState {
    const fresh: NeedsState = { thirst: 0.2, water: 1, lastBrushed: Date.now(), dirty: false, limping: false, lowCareDays: 0, lastDayChecked: '', at: Date.now() };
    try {
      const raw = localStorage.getItem(this.saveKey);
      if (!raw) return fresh;
      const s = { ...fresh, ...(JSON.parse(raw) as Partial<NeedsState>) };
      const away = Math.min(86400, (Date.now() - s.at) / 1000);
      s.thirst = Math.min(1, s.thirst + away / THIRST_PERIOD);
      return s;
    } catch {
      return fresh;
    }
  }

  save(): void {
    this.state.at = Date.now();
    try {
      localStorage.setItem(this.saveKey, JSON.stringify(this.state));
    } catch {
      /* no storage */
    }
    this.onChange?.();
  }

  snapshot(): NeedsState {
    return { ...this.state, at: Date.now() };
  }

  applyRemote(s: NeedsState): void {
    if (s.at <= this.state.at) return;
    this.state = { ...this.state, ...s };
    const away = Math.min(86400, (Date.now() - s.at) / 1000);
    this.state.thirst = Math.min(1, s.thirst + away / THIRST_PERIOD);
    this.applyLooks();
    this.save();
  }

  /** 0 fresh .. 1 scruffy */
  get scruff(): number {
    return Math.min(1, (Date.now() - this.state.lastBrushed) / (SCRUFF_DAYS * 86400000));
  }

  statusWords(): string[] {
    const w: string[] = [];
    if (this.state.thirst > 0.75) w.push('thirsty');
    if (this.state.dirty) w.push('needs a bath');
    else if (this.scruff > 0.7) w.push('scruffy');
    if (this.state.limping) w.push('limping');
    return w;
  }

  private applyLooks(): void {
    this.water.visible = this.state.water > 0.03;
    this.water.scale.set(0.5 + 0.5 * this.state.water, 1, 0.5 + 0.5 * this.state.water);
    this.water.position.y = 0.03 + 0.03 * this.state.water;
    this.dog.setCoat(this.state.dirty ? 1 : this.scruff * 0.5);
    this.dog.limping = this.state.limping;
  }

  // ---------- taps
  tapWater(): void {
    if (this.state.water > 0.9) {
      this.onMessage?.('the water bowl is full');
      return;
    }
    this.state.water = 1;
    this.applyLooks();
    this.onMessage?.('water bowl filled');
    this.onCare?.('play');
    this.dog.notice(this.waterBowl.position);
    this.save();
  }

  /** the till: return false to refuse (not enough credits); the vet is never refused */
  onPay?: (kind: CareAction) => boolean;

  /** brush / bath / vet: a short ritual; returns false if it's not needed / possible now */
  start(kind: CareAction): boolean {
    if (this.ritual) return false;
    if (kind === 'vet' && !this.state.limping) {
      this.onMessage?.("she's fine — no vet needed");
      return false;
    }
    if (kind === 'bath' && !this.state.dirty) {
      this.onMessage?.("she's clean — a brush would do");
      return false;
    }
    if (kind === 'brush' && this.scruff < 0.05 && !this.state.dirty) {
      this.onMessage?.('just brushed — she enjoys it anyway');
    }
    if (this.onPay && !this.onPay(kind)) return false;
    this.dog.groom(true, kind);
    this.phase = 'idle';
    this.ritual = { kind, t: 0, dur: kind === 'brush' ? 6 : kind === 'bath' ? 8 : 10 };
    this.buildProps(kind);
    const sound = kind === 'brush' ? 'brush' : kind === 'bath' ? 'splash' : null;
    if (sound) this.dog.sounds.play(sound, 0.5);
    return true;
  }

  private finish(): void {
    const r = this.ritual!;
    this.ritual = null;
    this.dog.groom(false, r.kind);
    this.clearProps();
    switch (r.kind) {
      case 'brush':
        this.state.lastBrushed = Date.now();
        this.onMessage?.('brushed — she looks great');
        this.onCare?.('groom');
        break;
      case 'bath':
        this.state.dirty = false;
        this.state.lastBrushed = Date.now();
        this.dog.shakeOff();
        this.onMessage?.('clean! …and she shook it all over you');
        this.onCare?.('groom');
        break;
      case 'vet':
        this.state.limping = false;
        this.state.lowCareDays = 0;
        this.onMessage?.('the vet says she just needed looking after');
        this.onCare?.('vet');
        break;
    }
    this.applyLooks();
    this.save();
  }

  /** the daily check: poor care for days → limping */
  dailyCheck(today: string, quality: number): void {
    if (this.state.lastDayChecked === today) return;
    if (this.state.lastDayChecked) {
      this.state.lowCareDays = quality < 0.15 ? this.state.lowCareDays + 1 : 0;
      if (this.state.lowCareDays >= 3 && !this.state.limping) {
        this.state.limping = true;
        this.onMessage?.("she's limping — she needs the vet");
      }
    }
    this.state.lastDayChecked = today;
    this.applyLooks();
    this.save();
  }

  // ---------- what you see during a ritual
  private buildProps(kind: CareAction): void {
    this.clearProps();
    const g = this.props;
    if (kind === 'brush') {
      // a wooden brush with bristles, held above her back
      const handle = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.035, 0.09), new THREE.MeshStandardMaterial({ color: 0x8a5a3c, roughness: 0.8 }));
      g.add(handle);
      const bristles = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.03, 0.08), new THREE.MeshStandardMaterial({ color: 0x3a3a3a, roughness: 1 }));
      bristles.position.y = -0.03;
      g.add(bristles);
      // loose fur drifting off
      const n = 40;
      const pos = new Float32Array(n * 3);
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      const coat = new THREE.Color(this.dog.profile.coat);
      this.fur = new THREE.Points(geo, new THREE.PointsMaterial({ color: coat, size: 0.03, transparent: true, opacity: 0.8, depthWrite: false }));
      this.fur.frustumCulled = false;
      this.dropVel = new Float32Array(n * 3);
      for (let i = 0; i < n; i++) this.respawnFur(i);
      this.scene.add(this.fur);
    } else if (kind === 'bath') {
      // a shower head above her, water drops, suds on her back
      const head = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.09, 0.04, 16), new THREE.MeshStandardMaterial({ color: 0xc9ced4, metalness: 0.7, roughness: 0.3 }));
      g.add(head);
      const pipe = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.015, 0.3, 8), head.material);
      pipe.position.y = 0.17;
      g.add(pipe);
      const n = 220;
      const pos = new Float32Array(n * 3);
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      this.drops = new THREE.Points(geo, new THREE.PointsMaterial({ color: 0xbfe3ff, size: 0.028, transparent: true, opacity: 0.85, depthWrite: false }));
      this.drops.frustumCulled = false;
      this.dropVel = new Float32Array(n * 3);
      for (let i = 0; i < n; i++) this.respawnDrop(i, true);
      this.scene.add(this.drops);
      const foam = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.6, transparent: true, opacity: 0.92 });
      for (let i = 0; i < 9; i++) {
        const b = new THREE.Mesh(new THREE.SphereGeometry(0.045 + Math.random() * 0.035, 8, 6), foam);
        b.userData.seed = Math.random();
        b.scale.setScalar(0.01);
        this.suds.push(b);
        this.scene.add(b);
      }
    } else {
      // a stethoscope: a chest piece on a tube, held to her side
      const disc = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.015, 16), new THREE.MeshStandardMaterial({ color: 0xd8d8d8, metalness: 0.8, roughness: 0.25 }));
      disc.rotation.x = Math.PI / 2;
      g.add(disc);
      const tube = new THREE.Mesh(new THREE.TorusGeometry(0.12, 0.008, 6, 24, Math.PI * 0.9), new THREE.MeshStandardMaterial({ color: 0x2b2b2b, roughness: 0.6 }));
      tube.position.set(0.05, 0.12, -0.02);
      tube.rotation.z = -0.4;
      g.add(tube);
    }
    g.visible = true;
  }

  private clearProps(): void {
    this.props.visible = false;
    for (const c of [...this.props.children]) {
      this.props.remove(c);
      (c as THREE.Mesh).geometry?.dispose();
    }
    for (const p of [this.drops, this.fur]) {
      if (p) {
        this.scene.remove(p);
        p.geometry.dispose();
      }
    }
    this.drops = null;
    this.fur = null;
    this.dropVel = null;
    for (const b of this.suds) {
      this.scene.remove(b);
      b.geometry.dispose();
    }
    this.suds = [];
  }

  /** her back, in world space: root + a point along her heading */
  private onHer(out: THREE.Vector3, back: number, up: number, side = 0): THREE.Vector3 {
    const s = this.dog.profile.size;
    const h = this.dog.root.rotation.y;
    return out
      .set(side * s, up * s, back * s)
      .applyAxisAngle(_up, h)
      .add(this.dog.root.position);
  }

  private respawnDrop(i: number, anywhere: boolean): void {
    const pos = this.drops!.geometry.getAttribute('position') as THREE.BufferAttribute;
    this.onHer(_p, (Math.random() - 0.5) * 0.5, anywhere ? 0.3 + Math.random() * 0.9 : 1.2, (Math.random() - 0.5) * 0.35);
    pos.setXYZ(i, _p.x, _p.y, _p.z);
    const v = this.dropVel!;
    v[i * 3] = (Math.random() - 0.5) * 0.15;
    v[i * 3 + 1] = -1.4 - Math.random() * 0.8;
    v[i * 3 + 2] = (Math.random() - 0.5) * 0.15;
  }

  private respawnFur(i: number): void {
    const pos = this.fur!.geometry.getAttribute('position') as THREE.BufferAttribute;
    this.onHer(_p, (Math.random() - 0.5) * 0.5, 0.55, (Math.random() - 0.5) * 0.25);
    pos.setXYZ(i, _p.x, _p.y, _p.z);
    const v = this.dropVel!;
    v[i * 3] = (Math.random() - 0.5) * 0.4;
    v[i * 3 + 1] = 0.1 + Math.random() * 0.25;
    v[i * 3 + 2] = (Math.random() - 0.5) * 0.4;
  }

  private animateProps(dt: number): void {
    const r = this.ritual!;
    const t = r.t;
    const s = this.dog.profile.size;
    if (r.kind === 'brush') {
      // long strokes from shoulders to rump, lifting between strokes
      const u = (t * 0.9) % 1;
      const lift = u > 0.8 ? (u - 0.8) * 5 : 0;
      this.onHer(this.props.position, 0.25 - u * 0.55, 0.62 + lift * 0.18 + (this.dog.sitting > 0.5 ? -0.05 : 0), 0.05 * Math.sin(t * 3));
      this.props.rotation.set(0.1, this.dog.root.rotation.y, 0.2 * Math.sin(t * 6));
      if (this.fur && this.dropVel) {
        const pos = this.fur.geometry.getAttribute('position') as THREE.BufferAttribute;
        const v = this.dropVel;
        for (let i = 0; i < pos.count; i++) {
          v[i * 3 + 1] -= 0.25 * dt;
          pos.setXYZ(i, pos.getX(i) + v[i * 3] * dt, pos.getY(i) + v[i * 3 + 1] * dt, pos.getZ(i) + v[i * 3 + 2] * dt);
          if (pos.getY(i) < 0.02 || Math.random() < 0.4 * dt) this.respawnFur(i);
        }
        pos.needsUpdate = true;
      }
    } else if (r.kind === 'bath') {
      this.onHer(this.props.position, 0.05 + 0.15 * Math.sin(t * 0.7), 1.25, 0.1 * Math.sin(t * 0.9));
      this.props.rotation.set(0.15 * Math.sin(t * 1.3), this.dog.root.rotation.y, 0);
      this.dog.soak(Math.min(1, t / 3));
      if (this.drops && this.dropVel) {
        const pos = this.drops.geometry.getAttribute('position') as THREE.BufferAttribute;
        const v = this.dropVel;
        for (let i = 0; i < pos.count; i++) {
          v[i * 3 + 1] -= 4 * dt;
          pos.setXYZ(i, pos.getX(i) + v[i * 3] * dt, pos.getY(i) + v[i * 3 + 1] * dt, pos.getZ(i) + v[i * 3 + 2] * dt);
          if (pos.getY(i) < 0.01) this.respawnDrop(i, false);
        }
        pos.needsUpdate = true;
      }
      // suds grow over the first half, wash away over the last two seconds
      const grow = Math.min(1, t / (r.dur * 0.5));
      const wash = Math.max(0, 1 - Math.max(0, t - (r.dur - 2)) / 2);
      this.suds.forEach((b, i) => {
        const a = (i / this.suds.length) * Math.PI * 2 + b.userData.seed;
        this.onHer(b.position, 0.2 * Math.cos(a) - 0.05, 0.5 + 0.08 * Math.sin(a * 2), 0.14 * Math.sin(a));
        b.position.y += 0.03 * Math.sin(t * 2 + b.userData.seed * 6);
        b.scale.setScalar(Math.max(0.01, grow * wash) * s);
      });
    } else {
      // the stethoscope moves to a few spots on her chest and side, pausing at each
      const spot = Math.floor(t / 2.5) % 4;
      const spots: [number, number, number][] = [
        [0.35, 0.45, 0.16],
        [0.15, 0.5, 0.2],
        [-0.1, 0.48, 0.2],
        [0.3, 0.38, -0.16],
      ];
      const [back, up, side] = spots[spot];
      this.onHer(_p, back, up, side);
      this.props.position.lerp(_p, 1 - Math.exp(-6 * dt));
      this.props.rotation.set(0, this.dog.root.rotation.y + (side > 0 ? Math.PI / 2 : -Math.PI / 2), 0);
    }
  }

  // ---------- per frame
  update(dt: number, cameraPos: THREE.Vector3): void {
    const dog = this.dog;
    this.state.thirst = Math.min(1, this.state.thirst + dt / THIRST_PERIOD);

    if (this.ritual) {
      this.ritual.t += dt;
      const n = Math.round((this.ritual.t / this.ritual.dur) * 8);
      const label = { brush: 'brushing', bath: 'bath time', vet: 'at the vet' }[this.ritual.kind];
      this.onMessage?.(`${label}… ${'▮'.repeat(n)}${'▯'.repeat(8 - n)}`);
      this.animateProps(dt);
      if (this.ritual.t >= this.ritual.dur) this.finish();
      return;
    }

    // rolling in something: now and then while she's sniffing about the yard
    this.rollCheck += dt;
    if (this.rollCheck > 5) {
      this.rollCheck = 0;
      if (!this.state.dirty && dog.sniffingNow && Math.random() < 0.02) {
        this.state.dirty = true;
        dog.rollInIt();
        this.onMessage?.('oh no — she rolled in something');
        this.applyLooks();
        this.save();
      }
    }

    // drinking
    const near = dog.root.position.distanceTo(this.waterBowl.position) < REACH + 0.25;
    switch (this.phase) {
      case 'idle':
        if (this.state.thirst > DRINK_AT && this.state.water > 0.03 && dog.idle) {
          dog.goTo(this.approach(_p));
          this.phase = 'walking';
        }
        break;
      case 'walking':
        if (!dog.moving) {
          if (near && this.state.water > 0.03) {
            dog.startEating(this.waterBowl.position, 'drink');
            this.phase = 'drinking';
            this.sipTimer = 0;
            dog.sounds.play('lap', 0.5);
          } else this.phase = 'idle';
        }
        break;
      case 'drinking':
        this.sipTimer += dt;
        if (this.sipTimer > SIP_EVERY) {
          this.sipTimer = 0;
          this.state.water = Math.max(0, this.state.water - SIP_WATER);
          this.state.thirst = Math.max(0, this.state.thirst - SIP_THIRST);
          dog.sip();
          this.applyLooks();
        }
        if (!dog.eating || this.state.thirst < 0.05 || this.state.water <= 0.03) {
          dog.stopEating(cameraPos);
          this.phase = 'idle';
          this.save();
        }
        break;
    }
  }

  private approach(out: THREE.Vector3): THREE.Vector3 {
    out.subVectors(this.dog.root.position, this.waterBowl.position).setY(0);
    if (out.lengthSq() < 1e-4) out.set(0, 0, 1);
    return out.setLength(REACH).add(this.waterBowl.position).setY(0);
  }
}
